import React, { useState } from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Button } from '@mui/material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookDemoModal from '../components/BookDemoModal';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SecurityIcon from '@mui/icons-material/Security';
import StarIcon from '@mui/icons-material/Star';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import aboutDoctorImg from '../assets/about_doctor.jpg';

const TEAL = '#0B6B6B';
const TEAL_DARK = '#094F4F';
const GREEN = '#25D366';
const NAVY = '#1A2F3A';

export default function ForDoctorsPage() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <Navbar />

      {/* Hero Banner Section */}
      <Box 
        sx={{ 
          position: 'relative',
          backgroundColor: NAVY,
          color: '#ffffff',
          overflow: 'hidden',
          minHeight: { xs: 'auto', md: '500px' }
        }}
      >
        {/* Desktop absolute background image on the right */}
        <Box
          component="img"
          src={aboutDoctorImg}
          alt="Doctors Workflow Management"
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            width: '52%',
            objectFit: 'cover',
            objectPosition: 'center top',
            zIndex: 0,
            maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.15) 15%, black 35%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.15) 15%, black 35%)',
          }}
        />

        {/* Content Container */}
        <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 }, pt: { xs: 16, md: 22 }, pb: { xs: 8, md: 12 }, position: 'relative', zIndex: 1 }}>
          <Grid container>
            <Grid size={{ xs: 12, md: 6.5 }}>
              {/* WhatsApp badge */}
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.25)', borderRadius: '50px', px: 2, py: 0.7, mb: 3.5, width: 'fit-content' }}>
                <WhatsAppIcon sx={{ color: GREEN, fontSize: 18 }} />
                <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>Automated Vitals Check-in</Typography>
              </Box>

              <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: { xs: '38px', md: '56px' }, fontWeight: 800, lineHeight: 1.1, mb: 3, letterSpacing: '-1px' }}>
                Designed for Doctors.<br />
                Loved by Patients.
              </Typography>
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '16px', color: 'rgba(255,255,255,0.65)', mb: 4.5, lineHeight: 1.7, maxWidth: '540px' }}>
                Care Beyond automates vital collection, medication reminders, and patient check-ins directly through WhatsApp. Increase clinic efficiency and patient adherence.
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => setDemoOpen(true)}
                sx={{ 
                  backgroundColor: TEAL, 
                  color: '#ffffff', 
                  fontFamily: 'Plus Jakarta Sans, sans-serif', 
                  fontWeight: 700, 
                  textTransform: 'none',
                  px: 4.5, 
                  py: 1.6, 
                  borderRadius: '10px', 
                  fontSize: '15px',
                  boxShadow: '0 8px 28px rgba(11,107,107,0.5)',
                  '&:hover': { backgroundColor: TEAL_DARK, transform: 'translateY(-1px)', boxShadow: '0 12px 32px rgba(11,107,107,0.6)' },
                  transition: 'all 0.2s ease'
                }}
              >
                Book a Free Demo
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits cards */}
      <Container maxWidth="xl" sx={{ py: 12, px: { xs: 3, md: 8 }, flexGrow: 1 }}>
        <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: { xs: '28px', md: '36px' }, fontWeight: 800, color: NAVY, textAlign: 'center', mb: 1.5 }}>
          Why Clinics Choose Care Beyond
        </Typography>
        <Typography sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '15px', color: '#64748b', textAlign: 'center', mb: 8, maxWidth: '600px', mx: 'auto' }}>
          Reduce administrative overhead and improve outcomes with direct patient monitoring built specifically for your practice workflow.
        </Typography>

        <Grid container spacing={4}>
          {[
            { icon: <LocalHospitalIcon sx={{ fontSize: 28, color: TEAL }} />, title: 'Vitals Auto-Collection', desc: 'No more manual logging. Vitals like blood pressure, glucose levels, and weight are logged automatically via WhatsApp conversations.' },
            { icon: <NotificationsActiveIcon sx={{ fontSize: 28, color: '#ef4444' }} />, title: 'Emergency Alerts', desc: 'Get notified instantly if a patient reports values that exceed threshold levels. Care Beyond acts as your virtual alert nurse.' },
            { icon: <SecurityIcon sx={{ fontSize: 28, color: '#10b981' }} />, title: 'HIPAA Compliant', desc: 'Patient health information is fully encrypted and secure. All exchanges strictly adhere to clinical compliance guidelines.' },
            { icon: <StarIcon sx={{ fontSize: 28, color: '#f59e0b' }} />, title: 'Clinical Integration', desc: 'Synchronize vital logs, notes, and medication changes into your clinic dashboard or existing EMR/EHR provider systems.' }
          ].map((item, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
              <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 30px rgba(0,0,0,0.04)' } }}>
                <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '12px', backgroundColor: 'rgba(11,107,107,0.06)' }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '18px', fontWeight: 700, color: NAVY }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '14px', color: '#64748b', lineHeight: 1.65 }}>
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Footer onOpenDemo={() => setDemoOpen(true)} />
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </Box>
  );
}
